import { createBrowserRouter, redirect } from "react-router-dom";
import { api } from "./lib/api.ts";
import { Root } from "./routes/root.tsx";
import { ErrorPage } from "./routes/error.tsx";

interface Me {
  user: { id: string; email: string; name: string } | null;
  org: { id: string; name: string; plan: string; role: string } | null;
}

async function meLoader(): Promise<Me> {
  try {
    return await api.get<Me>("/me");
  } catch {
    return { user: null, org: null };
  }
}

async function requireAuth() {
  const me = await meLoader();
  if (!me.user) throw redirect("/sign-in");
  if (!me.org) throw redirect("/onboarding");
  return me;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        loader: async () => {
          const me = await meLoader();
          if (!me.user) throw redirect("/sign-in");
          if (!me.org) throw redirect("/onboarding");
          throw redirect("/dashboard");
        },
        element: null,
      },
      {
        path: "sign-in",
        lazy: async () => ({
          Component: (await import("./routes/sign-in.tsx")).SignIn,
        }),
      },
      {
        path: "sign-up",
        lazy: async () => ({
          Component: (await import("./routes/sign-up.tsx")).SignUp,
        }),
      },
      {
        path: "onboarding",
        lazy: async () => ({
          Component: (await import("./routes/onboarding.tsx")).Onboarding,
        }),
      },
      {
        loader: requireAuth,
        lazy: async () => ({
          Component: (await import("./routes/app-shell.tsx")).AppShell,
        }),
        children: [
          {
            path: "dashboard",
            lazy: async () => ({
              Component: (await import("./routes/dashboard.tsx")).Dashboard,
            }),
          },
          {
            path: "envelope/:envelopeId",
            lazy: async () => ({
              Component: (await import("./routes/envelope-detail.tsx"))
                .EnvelopeDetail,
            }),
          },
          {
            path: "entities/:entityId",
            children: [
              { index: true, loader: () => redirect("inbox") },
              {
                path: "inbox",
                lazy: async () => ({
                  Component: (await import("./routes/entity-inbox.tsx")).Inbox,
                }),
              },
              {
                path: "transactions/new",
                lazy: async () => ({
                  Component: (await import("./routes/transaction-new.tsx")).NewTransaction,
                }),
              },
              {
                path: "transactions/pending",
                lazy: async () => ({
                  Component: (await import("./routes/pending-allocations.tsx"))
                    .PendingAllocations,
                }),
              },
              {
                path: "reports/pnl",
                lazy: async () => ({
                  Component: (await import("./routes/report-pnl.tsx")).Pnl,
                }),
              },
              {
                path: "import",
                lazy: async () => ({
                  Component: (await import("./routes/import-csv.tsx")).ImportCsv,
                }),
              },
              {
                path: "banks",
                lazy: async () => ({
                  Component: (await import("./routes/entity-banks.tsx"))
                    .EntityBanks,
                }),
              },
              {
                path: "year-end",
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import("./routes/year-end.tsx")).YearEnd,
                    }),
                  },
                  {
                    path: "form-5472",
                    lazy: async () => ({
                      Component: (await import("./routes/year-end.tsx"))
                        .Form5472Placeholder,
                    }),
                  },
                  {
                    path: "form-1120",
                    lazy: async () => ({
                      Component: (await import("./routes/year-end.tsx"))
                        .Form1120Placeholder,
                    }),
                  },
                  {
                    path: "schedule-c",
                    lazy: async () => ({
                      Component: (await import("./routes/year-end.tsx"))
                        .ScheduleCPlaceholder,
                    }),
                  },
                ],
              },
              {
                path: "budget",
                lazy: async () => ({
                  Component: (await import("./routes/entity-budget.tsx")).EntityBudget,
                }),
              },
              {
                path: "reconcile",
                lazy: async () => ({
                  Component: (await import("./routes/reconcile.tsx")).Reconcile,
                }),
              },
              {
                path: "scheduled",
                lazy: async () => ({
                  Component: (await import("./routes/entity-scheduled.tsx")).EntityScheduled,
                }),
              },
              {
                path: "rules",
                lazy: async () => ({
                  Component: (await import("./routes/entity-rules.tsx")).EntityRules,
                }),
              },
              {
                path: "tax-settings",
                lazy: async () => ({
                  Component: (
                    await import("./routes/entity-tax-settings.tsx")
                  ).EntityTaxSettings,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
]);
